import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Autocomplete, Box, IconButton, Link as MuiLink, Stack, TextField, Typography } from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { createScopedLogger } from "../../../common";
import CopyToClipboardButton from "../../../common/components/CopyToClipboardButton";
import { formatString } from "../../../common/formatString";
import { KeyNames, useDebouncedCallback, useErrorHandler } from "../../../common/hooks";
import { IExpressionEvaluatorContext } from "../../../ExpressionsModule";
import { VariableNames } from "../../Constants/VariableNames";
import useMaskPiiSetting from "../../Hooks/useMaskPiiSetting";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { PropertyDisplayFormat } from "../../Models/PropertyDisplayFormat";
import { PropertyDisplayModel } from "../../Models/PropertyDisplayModel";
import { formatDisplayValue } from "../../Utils/displayFormatting";
import formatPropertyValue from "./formatPropertyValue";
import { formatPhoneNumber } from "./phoneNumberFormatting";
import { hasRequiredPropertyValue } from "./requiredPropertyValue";

function normalizeEmailInput(value: string): string {
  return value.trim().toLowerCase();
}

type DisplayPropertyValueProps = {
  data: any;
  property: PropertyDisplayModel;
  context: IExpressionEvaluatorContext;
  phoneNumberFormat?: string;
  currencyCode?: string;
  isRowDirection: boolean;
  isEditing: boolean;
  editValue: string | Dayjs | number | null;
  hasChanged: boolean;
  isUpdating: boolean;
  hideSaveCancel?: boolean;
  onEditChange: (value: string | Dayjs | number | null) => void;
  onSave: () => void;
  onCancel: () => void;
  onEnterEditMode: () => void;
  onEnterBulkEditMode?: () => void;
  autoEnterEditMode?: boolean;
  observedPaths?: string[];
};

const DisplayPropertyValue: React.FC<DisplayPropertyValueProps> = ({
  data,
  property,
  context,
  phoneNumberFormat,
  currencyCode,
  isRowDirection,
  isEditing,
  editValue,
  hasChanged,
  isUpdating,
  hideSaveCancel,
  onEditChange,
  onSave,
  onCancel,
  onEnterEditMode,
  onEnterBulkEditMode,
  autoEnterEditMode = true,
  observedPaths,
}) => {
  const errorHandler = useErrorHandler();
  const [maskPii] = useMaskPiiSetting();

  // Unique identifier for this property instance
  const componentId = `DataDisplayPropertyValue-${property.Label}`;
  const logger = createScopedLogger(componentId);

  // Lookup search state (per property instance)
  const [lookupOptions, setLookupOptions] = useState<any[]>([]);
  const [lookupOptionsPending, setLookupOptionsPending] = useState<boolean>(false);
  const [lookupInputValue, setLookupInputValue] = useState<string>("");

  // Use useObservedExpression to reactively evaluate the Disabled expression
  const [isDisabled] = useObservedExpression<boolean>(property.Disabled, context, componentId, (value) => !!value);
  const [isRequired] = useObservedExpression<boolean>(property.Required, context, `${componentId}-Required`, (value) => !!value);

  // Evaluate Value expression to check if there's a lookup value
  const [lookupValue] = useObservedExpression<any>(property.Lookup?.Value, context, `${componentId}-Value`);

  // Always evaluate Item expression, but we'll only use the result if Value is non-null/undefined
  const [lookupDisplayItemRaw] = useObservedExpression<any>(property.Lookup?.Item, context, `${componentId}-Lookup`);

  // Only use the item if there's a corresponding value
  const lookupDisplayItem = lookupValue !== null && lookupValue !== undefined ? lookupDisplayItemRaw : undefined;

  // Load lookup items function
  const loadLookupItems = useCallback(
    async (searchText: string | null) => {
      if (!property.Lookup?.Items) {
        logger.warn("Lookup Items expression is not defined.");
        return;
      }

      setLookupOptionsPending(true);
      try {
        const eventContext = context.CreateChild();
        eventContext.VariableScope.set(VariableNames.Event, { text: searchText || null });

        const result = await eventContext.Evaluator.evaluate(property.Lookup.Items, eventContext);

        if (result === undefined || result === null) {
          setLookupOptions([]);
          return;
        }

        if (!Array.isArray(result)) {
          logger.error("Lookup Items expression did not return an array:", result);
          throw new Error("Lookup Items expression did not return an array: " + result);
        }

        logger.debug("Loaded lookup options:", result);
        setLookupOptions(result);
      } catch (error) {
        errorHandler(error);
        setLookupOptions([]);
      } finally {
        setLookupOptionsPending(false);
      }
    },
    [property.Lookup?.Items, context, errorHandler, logger]
  );

  // Debounced version of loadLookupItems for typing
  const debouncedLoadLookupItems = useDebouncedCallback(loadLookupItems, 300);

  // Load initial lookup options when entering edit mode
  useEffect(() => {
    if (isEditing && property.Lookup) {
      loadLookupItems(null);
    }
  }, [isEditing, property.Lookup, loadLookupItems]);

  const handleLookupInputChange = useCallback(
    (newInputValue: string, reason: string) => {
      setLookupInputValue(newInputValue);

      // Only trigger search when user is typing (reason === "input")
      if (reason === "input") {
        debouncedLoadLookupItems(newInputValue);
      }
    },
    [debouncedLoadLookupItems]
  );

  const handleLookupOpen = useCallback(() => {
    // When dropdown opens, clear input text
    setLookupInputValue("");

    // Load options with null search if not already loaded
    if (lookupOptions.length === 0 && !lookupOptionsPending) {
      loadLookupItems(null);
    }
  }, [lookupOptions, lookupOptionsPending, loadLookupItems]);

  // Memoize lookup helper functions (must be at top level, not inside conditional)
  const getItemKey = useCallback(
    (item: any) => {
      const keyField = property.Lookup?.ItemKeyField || "Id";
      return item?.[keyField];
    },
    [property.Lookup?.ItemKeyField]
  );

  const getItemLabel = useCallback(
    (item: any) => {
      if (!item) return "";
      const itemLabel = property.Lookup?.ItemLabel || "{Label}";
      return formatString(itemLabel, item);
    },
    [property.Lookup?.ItemLabel]
  );

  const valueObservedPaths = observedPaths ?? [];
  const value = formatPropertyValue(data, property, valueObservedPaths, lookupDisplayItem, phoneNumberFormat, currencyCode);
  const displayValue = formatDisplayValue(
    value,
    { format: undefined },
    {
      maskPii,
      fieldPath: property.Field,
      label: property.Label,
      observedPaths: valueObservedPaths,
      format: property.Format,
    }
  );
  const isEditable = !isDisabled && (!!property.UpdateData || !!property.Lookup);
  const isRequiredDisplayEmpty = !!isRequired && !value;
  const isRequiredEditEmpty = !!isRequired && !hasRequiredPropertyValue(editValue);

  const textSx = {
    fontWeight: "bold",
    flex: 1,
    overflow: isRowDirection ? "hidden" : "epllipsis",
    textOverflow: isRowDirection ? "ellipsis" : undefined,
    textDecoration: "none",
    cursor: (isEditable || !!onEnterBulkEditMode) && autoEnterEditMode ? "pointer" : "default",
  };

  if (isEditing) {
    const isDateFormat = property.Format === PropertyDisplayFormat.Date;
    const isDateTimeFormat = property.Format === PropertyDisplayFormat.DateTime;
    const isCurrencyFormat = property.Format === PropertyDisplayFormat.Currency;
    const isPhoneNumberFormat = property.Format === PropertyDisplayFormat.PhoneLink;
    const isEmailFormat = property.Format === PropertyDisplayFormat.EmailLink;
    const isLookupField = !!property.Lookup;

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === KeyNames.Enter && !isUpdating) {
        if (hasChanged) {
          onSave();
        } else {
          onCancel();
        }
      }
    };

    return (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        {isLookupField ? (
          <Autocomplete
            value={editValue}
            onChange={(event, newValue) => onEditChange(newValue)}
            inputValue={lookupInputValue}
            onInputChange={(event, newInputValue, reason) => handleLookupInputChange(newInputValue, reason)}
            onOpen={handleLookupOpen}
            options={lookupOptions}
            size="small"
            getOptionKey={getItemKey}
            getOptionLabel={getItemLabel}
            loading={lookupOptionsPending}
            disabled={isUpdating}
            renderInput={(params) => <TextField {...params} autoFocus error={isRequiredEditEmpty} />}
            sx={{ flex: 1, minWidth: 0, width: "100%" }}
          />
        ) : isDateFormat ? (
          <DatePicker
            value={editValue as Dayjs | null}
            onChange={(newValue: Dayjs | null) => onEditChange(newValue)}
            disabled={isUpdating}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                autoFocus: true,
                error: isRequiredEditEmpty,
              },
            }}
            sx={{ minWidth: 0, width: "180px", flex: 1 }}
          />
        ) : isDateTimeFormat ? (
          <DateTimePicker
            value={editValue as Dayjs | null}
            onChange={(newValue: Dayjs | null) => onEditChange(newValue)}
            disabled={isUpdating}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                autoFocus: true,
                error: isRequiredEditEmpty,
              },
            }}
            sx={{ minWidth: 0, width: "180px", flex: 1 }}
          />
        ) : isCurrencyFormat ? (
          <TextField
            value={editValue as number | string}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string, numbers, and decimal point
              if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                onEditChange(value === "" ? "" : value);
              }
            }}
            type="text"
            inputMode="decimal"
            size="small"
            fullWidth
            autoFocus
            disabled={isUpdating}
            error={isRequiredEditEmpty}
            sx={{ flex: 1, minWidth: 0 }}
            onKeyDown={handleKeyDown}
          />
        ) : isPhoneNumberFormat ? (
          <TextField
            value={editValue as string}
            onChange={(e) => onEditChange(formatPhoneNumber(e.target.value, phoneNumberFormat))}
            type="tel"
            inputMode="tel"
            size="small"
            fullWidth
            autoFocus
            disabled={isUpdating}
            error={isRequiredEditEmpty}
            sx={{ flex: 1, minWidth: 0 }}
            onKeyDown={handleKeyDown}
          />
        ) : isEmailFormat ? (
          <TextField
            value={editValue as string}
            onChange={(e) => onEditChange(normalizeEmailInput(e.target.value))}
            type="email"
            inputMode="email"
            size="small"
            fullWidth
            autoFocus
            disabled={isUpdating}
            error={isRequiredEditEmpty}
            sx={{ flex: 1, minWidth: 0 }}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <TextField
            value={editValue as string}
            onChange={(e) => onEditChange(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            disabled={isUpdating}
            error={isRequiredEditEmpty}
            sx={{ flex: 1, minWidth: 0 }}
            onKeyDown={handleKeyDown}
          />
        )}
        {!hideSaveCancel && (
          <>
            <IconButton size="small" onClick={onSave} color="primary" disabled={!hasChanged || isUpdating} loading={isUpdating}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancel} disabled={isUpdating}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0, width: "100%", minHeight: 28 }}
      title={isDisabled && property.DisabledTooltip ? property.DisabledTooltip : undefined}
      aria-label={isDisabled && property.DisabledTooltip ? property.DisabledTooltip : undefined}
      onClick={autoEnterEditMode ? (isEditable ? onEnterEditMode : onEnterBulkEditMode) : undefined}
    >
      <Box sx={{ minWidth: 0, overflow: "ellipsis" }}>
        {!value ? (
          <Typography
            variant="body2"
            sx={{ ...textSx, color: isRequiredDisplayEmpty ? "error.main" : "text.disabled", fontStyle: "italic", fontWeight: "normal" }}
          >
            {isRequiredDisplayEmpty ? "(required)" : "(empty)"}
          </Typography>
        ) : property.Format === PropertyDisplayFormat.EmailLink ? (
          <MuiLink href={`mailto:${value}`} variant="body2" sx={textSx}>
            {displayValue}
          </MuiLink>
        ) : property.Format === PropertyDisplayFormat.PhoneLink ? (
          <MuiLink href={`tel:${value.replace(/\D/g, "")}`} variant="body2" sx={textSx}>
            {displayValue}
          </MuiLink>
        ) : (
          <Typography variant="body2" sx={{ ...textSx, color: isRequiredDisplayEmpty ? "error.main" : undefined }}>
            {displayValue}
          </Typography>
        )}
      </Box>
      {value && <CopyToClipboardButton value={displayValue} />}
      <Box sx={{ flexGrow: 1 }} />
      {isEditable && (
        <IconButton size="small" onClick={onEnterEditMode}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default DisplayPropertyValue;
